import { getHours, getMinutes, set } from 'date-fns';
import React, { useMemo } from 'react';
import { useProviderConfig } from '../provider';
import { DisabledTimes } from './TimePicker';
import { TimeChipRow } from './TimeChipRow';
import { time_chip_input_root_css } from './styles';
import { getAvailableSelectedTime, getDisabledTimes, getTimePickerOptions } from './utils';

export interface TimeChipInputProps {
  /** 현재 선택된 시각입니다. null이면 미선택 상태입니다. */
  value: Date | null;
  /**
   * 시 또는 분 칩을 선택하면 호출됩니다.
   * 반대 축(분 또는 시)을 첫 유효값으로 자동 채운 완성 시각이 전달됩니다.
   */
  onChange: (value: Date) => void;
  /** 선택 불가한 시/분을 지정합니다. */
  disabledTime?: (value: Date | null) => DisabledTimes;
  /**
   * 분 선택 단위 배수입니다.
   *
   * @default 1
   */
  incrementMinute?: number;
  /**
   * 비활성화된 분 항목을 목록에서 숨길지 여부입니다.
   *
   * @default false
   */
  hideDisabledMinutes?: boolean;
  /** @default false */
  disabled?: boolean;
  className?: string;
}

export const TimeChipInput = ({
  value,
  onChange,
  disabledTime,
  incrementMinute,
  hideDisabledMinutes,
  disabled,
  className,
}: TimeChipInputProps) => {
  const { locale } = useProviderConfig();
  const hourUnit = locale?.TimePicker.unit.hour ?? '';
  const minuteUnit = locale?.TimePicker.unit.minute ?? '';

  const selectedHour = value != null ? getHours(value) : undefined;
  const selectedMinute = value != null ? getMinutes(value) : undefined;

  const { timeHourPickerOptions, timeMinutePickerOptions } = useMemo(() => {
    const disabledTimes = getDisabledTimes(disabledTime, value ?? new Date());
    return {
      timeHourPickerOptions: getTimePickerOptions(24, 1, disabledTimes.disabledHours),
      timeMinutePickerOptions: getTimePickerOptions(
        60,
        incrementMinute,
        disabledTimes.disabledMinutes,
        hideDisabledMinutes,
      ),
    };
  }, [disabledTime, value, incrementMinute, hideDisabledMinutes]);

  const buildTime = (hour: number, minute: number) => {
    const base = value ?? new Date();
    const { disabledMinutes } = getDisabledTimes(disabledTime, set(base, { hours: hour }));
    const minuteOptions = getTimePickerOptions(60, incrementMinute, disabledMinutes, hideDisabledMinutes);
    const validMinute = minuteOptions.some((option) => option.value === minute)
      ? minute
      : getAvailableSelectedTime(minuteOptions) ?? 0;
    return set(base, { hours: hour, minutes: validMinute, seconds: 0 });
  };

  const handleChangeHour = (hour: number) => {
    const minute = value != null ? getMinutes(value) : getAvailableSelectedTime(timeMinutePickerOptions) ?? 0;
    onChange(buildTime(hour, minute));
  };

  const handleChangeMinute = (minute: number) => {
    const hour = value != null ? getHours(value) : getAvailableSelectedTime(timeHourPickerOptions) ?? 0;
    onChange(buildTime(hour, minute));
  };

  return (
    <div css={time_chip_input_root_css} className={className}>
      <TimeChipRow
        unit={hourUnit}
        options={timeHourPickerOptions}
        selectedValue={selectedHour}
        disabled={disabled}
        onChange={handleChangeHour}
      />
      <TimeChipRow
        unit={minuteUnit}
        options={timeMinutePickerOptions}
        selectedValue={selectedMinute}
        disabled={disabled}
        onChange={handleChangeMinute}
      />
    </div>
  );
};
